import { namespace } from 'vuex-class';
import { Vue, Component, Prop } from 'vue-property-decorator';

import { ArticleType } from '@/@types/graphql-types';

import {
	GuideEntryFragment,
	GetGuideArticlesDocument,
	GetGuideArticlesQuery,
	GetGuideArticlesQueryVariables,
} from '@/pages/graphql/queries/GetGuideArticles.query';

type ArticleList = { articles: GuideEntryFragment[]; totalCount: number };

const Language = namespace('language');

@Component<UseArticleList>({
	name: 'UseArticleList',
	apollo: {
		articleList: {
			query: GetGuideArticlesDocument,
			variables(): GetGuideArticlesQueryVariables {
				return this.queryVariables;
			},
			update(data: GetGuideArticlesQuery) {
				return {
					articles: (data.articles.edges ?? []).map(({ node }) => node),
					totalCount: data.articles.totalCount,
				};
			},
			errorPolicy: 'all',
		},
	},
})
export default class UseArticleList extends Vue {
	@Prop({ default: 0 }) page: number;
	@Prop({ default: 30 }) pageSize: number;
	@Prop({ default: null }) entityId: null | string;

	@Language.Getter language: string;
	@Language.Getter country: string;

	articleList: ArticleList = {
		articles: [],
		totalCount: 0,
	};

	get lastPage() {
		return Math.ceil(this.articleList.totalCount / this.pageSize);
	}

	get queryVariables(): GetGuideArticlesQueryVariables {
		const variables = {
			country: this.country,
			language: this.language,
			first: this.pageSize,
			offset: Math.max(0, this.page - 1) * this.pageSize,
			filter: {
				articleTypes: [ArticleType.NewsArticle],
				linkedEntityIds: [] as string[],
			},
		};

		if (this.entityId) {
			variables.filter.linkedEntityIds.push(this.entityId);
		}

		return variables;
	}

	fetchPage() {
		this.$apollo.queries.articleList.fetchMore({
			variables: {
				first: this.pageSize,
				offset: (this.page + 1) * this.pageSize,
			},
			updateQuery(
				previousResult: GetGuideArticlesQuery,
				{ fetchMoreResult }: { fetchMoreResult?: GetGuideArticlesQuery }
			) {
				if (!fetchMoreResult) return previousResult;

				return {
					articles: (fetchMoreResult.articles.edges ?? []).map(({ node }) => node),
					totalCount: fetchMoreResult.articles.totalCount,
				};
			},
		});
	}

	get articles() {
		return this.articleList.articles.filter(article => article.content.fullPath !== this.$route.fullPath);
	}

	render() {
		if (this.$scopedSlots.default) {
			return this.$scopedSlots.default({
				articles: this.articles,
				lastPage: this.lastPage,
				totalCount: this.articleList.totalCount,
				fetchPage: this.fetchPage,
			});
		}

		return this.$slots[0];
	}
}
